/**
 * H21: the daily-activity keeper. Talking lists today's activities with status and a "go" hint: the local camp,
 * the next world boss, dance hours, the duel-win buff, the arena and ladders, the training boost. Reads state only.
 */
import { ARENA_HINT, DAILY_NPCS, DAILY_NPC_OFFSET, DAILY_NPC_SPRITE, DAILY_NPC_TITLE } from '../data/dailyNpc';
import { dailyEventsAt, eventHours, nextDailyEvent } from '../data/dailyEvents';
import { SMALL_DUNGEONS } from '../data/smallDungeons';
import { WB_HOME, bossAt, nextBoss, WORLD_BOSS } from '../data/worldBosses';
import { ZONES, type ZoneId } from '../data/zones';
import { DUEL_WIN_BOOST } from './duelWinBoost';
import { smallDungeonState } from './smallDungeons';
import type { Entity } from '../world';
import type { SystemHost, WorldSystem } from './types';

export class DailyNpcSystem implements WorldSystem {
  readonly name = 'dailyNpc';
  private npc: Entity | null = null;

  constructor(private readonly h: SystemHost) {}

  setup(): void {
    const w = this.h.world;
    const name = DAILY_NPCS[w.map.zone];
    if (!name || w.map.interior) return;
    this.npc = this.h.add({
      kind: 'npc',
      x: w.map.plaza.x + DAILY_NPC_OFFSET[0],
      y: w.map.plaza.y + DAILY_NPC_OFFSET[1],
      name,
      title: DAILY_NPC_TITLE,
      sprite: DAILY_NPC_SPRITE,
      facing: 1,
    });
  }

  /** The menu lines for today (Thai). */
  menu(): string[] {
    const w = this.h.world;
    const zone = w.map.zone as ZoneId;
    const sys = w.sys;
    const lines: string[] = [];
    // camp
    const camp = SMALL_DUNGEONS.find((c) => c.zone === zone);
    const st = smallDungeonState(w).find((c) => c.id === camp?.id);
    if (camp)
      lines.push(`• ดันเล็ก ${camp.name}: ${st?.claimed ? 'รับรางวัลวันนี้แล้ว' : st?.cleared ? 'กวาดแล้ว รอรับรางวัล' : 'ยังไม่รับรางวัลวันนี้'} · ดูเครื่องหมายค่ายบนแผนที่ (M)`);
    // world boss
    const up = bossAt(w.time);
    const nx = nextBoss(w.time);
    const bossLine = (id: keyof typeof WB_HOME, s: string) => `• ปีศาจใหญ่ ${WORLD_BOSS[id]?.name ?? id}: ${s} ที่ ${ZONES[WB_HOME[id]]?.name ?? WB_HOME[id]} (ต้องรวมกลุ่ม 4 คนขึ้นไป)`;
    lines.push(up ? bossLine(up.id, 'ปรากฏอยู่ตอนนี้') : bossLine(nx.id, `อีก ${Math.ceil(nx.in / 60)} นาที`));
    // dance / events
    const now = dailyEventsAt(zone, w.hour);
    const nxe = nextDailyEvent(zone, w.hour);
    if (now.length) lines.push(`• งานที่นี่ตอนนี้: ${now.map((d) => `${d.name} (${eventHours(d)})`).join(' · ')}`);
    else if (nxe) lines.push(`• งานถัดไป: ${nxe.event.name} (${eventHours(nxe.event)}) อีก ${Math.ceil(nxe.inHours)} ชม.`);
    lines.push('• รำหมู่: ชวนเพื่อนรำที่ลานกลางเมือง ได้ฝึกเพิ่มชั่วคราว');
    // duel buff
    const wins = sys.duelWinBoost.winsToday();
    lines.push(`• ท้าประลอง: ชนะวันนี้ ${wins}/${DUEL_WIN_BOOST.maxWins} (เหลือ ${Math.max(0, DUEL_WIN_BOOST.maxWins - wins)} ครั้งที่ได้ฝึกเพิ่ม)`);
    // arena
    lines.push(`• สนามประลองยุทธและบันได: ${ARENA_HINT}`);
    // sect dance (H23)
    const sd = w.sys.sectDance.line();
    if (sd) lines.push(sd);
    lines.push(...w.sys.bounties.lines());
    // training boost
    const b = sys.cultivation.boost.total();
    lines.push(`• ฝึกเพิ่ม: ${b > 0 ? `ใช้อยู่ +${Math.round(b * 100)}%` : 'ยังไม่มี'}`);
    return lines;
  }

  talk(npc: Entity): boolean {
    if (!this.npc || npc !== this.npc) return false;
    const paid = this.h.world.sys.bounties.claim();
    this.h.emit({ type: 'dialog', npcId: npc.id, text: `${paid.length ? paid.join('\n') + '\n' : ''}กิจกรรมวันนี้\n${this.menu().join('\n')}`, action: null });
    return true;
  }
}

/** I6: the same lines the keeper lists, for the "วันนี้" panel (reads state only). */
export const dailyLinesOf = (w: unknown): string[] =>
  (w as { sys: { dailyNpc: DailyNpcSystem } }).sys.dailyNpc.menu();
